import { useEffect, useSyncExternalStore } from 'react'
import { Icon } from './icon'

/** Тост исчезает через 1.9 с (`components/Toast`). */
const TOAST_DURATION_MS = 1900

type ToastState = { id: number; text: string } | null

let current: ToastState = null
let timer: ReturnType<typeof setTimeout> | null = null
const listeners = new Set<() => void>()

const emit = (): void => listeners.forEach((listener) => listener())

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener)

  return () => listeners.delete(listener)
}

const getSnapshot = (): ToastState => current

/**
 * Короткое подтверждение поверх экрана: «Код скопирован», «Подарок принят»,
 * `message` бэкенда после мутации. Новый тост сменяет прежний. Состояние —
 * модуль, а не Zustand: в сторах только профиль, язык и тема.
 */
export const showToast = (text: string): void => {
  current = { id: (current?.id ?? 0) + 1, text }
  emit()

  if (timer) {
    clearTimeout(timer)
  }

  timer = setTimeout(() => {
    current = null
    timer = null
    emit()
  }, TOAST_DURATION_MS)
}

/**
 * Место тоста — один раз в оболочке приложения. Внизу экрана, над системной
 * нижней панелью: в Telegram MainButton живёт вне WebView, поэтому отступ —
 * 16px и safe area, а не 100px макета (там тост стоит над нарисованной панелью).
 */
export const ToastHost = () => {
  const toast = useSyncExternalStore(subscribe, getSnapshot)

  useEffect(
    () => () => {
      if (timer) {
        clearTimeout(timer)
      }
    },
    [],
  )

  return (
    <div
      role="status"
      aria-live="polite"
      // Над своей нижней панелью (`BottomButton`, `--app-bottom-bar`), иначе — от низа экрана.
      className="pointer-events-none fixed inset-x-0 bottom-[calc(1rem+var(--app-bottom-bar,env(safe-area-inset-bottom,0px)))] z-30 flex justify-center px-2.5"
    >
      {toast && (
        <div
          key={toast.id}
          className="flex w-full max-w-app animate-toast-in items-center gap-2.5 rounded-section bg-toast-bg px-3.5 py-3 text-toast text-white"
        >
          <Icon name="checkCircle" size={20} />
          <span className="min-w-0 flex-1">{toast.text}</span>
        </div>
      )}
    </div>
  )
}
